import {
  chatControllerCreateChatMessage,
  chatControllerEditChatMessage,
  chatControllerGetCryptorChatMessage,
  type ChatMessage,
  type CreateChatMessage,
  type UpdateChatMessage,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getChatMessage = async (
  cryptorId: number,
  id: string,
): Promise<ChatMessage> =>
  unwrapApiResponse(
    chatControllerGetCryptorChatMessage<true>({
      path: { cryptorId, id: Number(id) },
      throwOnError: true,
    }),
  );

export const useGetChatMessage = (cryptorId: number, id: string) =>
  useQuery({
    initialData: null,
    queryKey: ["cryptors", cryptorId, "chat-message", id],
    queryFn: () => getChatMessage(cryptorId, id),
  });

const postChatMessage = async (cryptorId: number, data: CreateChatMessage) =>
  unwrapApiResponse(
    chatControllerCreateChatMessage<true>({
      body: data,
      path: { cryptorId },
      throwOnError: true,
    }),
  );

export const usePostChatMessage = (cryptorId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChatMessage) => postChatMessage(cryptorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cryptors", cryptorId, "chat-message"],
      });
    },
  });
};

const patchChatMessage = async (
  cryptorId: number,
  id: string,
  data: UpdateChatMessage,
) =>
  unwrapApiResponse(
    chatControllerEditChatMessage<true>({
      body: data,
      path: { cryptorId, id: Number(id) },
      throwOnError: true,
    }),
  );

export const usePatchChatMessage = (cryptorId: number, id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateChatMessage) =>
      patchChatMessage(cryptorId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cryptors", cryptorId, "chat-message"],
      });
    },
  });
};
